/**
 * General Configuration Validation
 * Validates common configuration fields (worker name, location, intervals, etc.)
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import {
  validateWorkerName,
  validateLocationName,
  validateTimeInterval,
} from '@packages/utils/device/commonValidation';
import type { FieldError } from '../../types/validation';
import { getByteLength } from './helpers';
import { DeviceType } from '@packages/types/device/common/deviceTypes';

/**
 * Custom hook for general configuration validation
 */
export const useGeneralValidation = (deviceType: DeviceType) => {
  const { t } = useTranslation('config');

  /**
   * Validate general configuration fields
   */
  const validateGeneralConfig = useCallback(
    (
      data: Record<string, unknown>
    ): { errors: Record<string, FieldError>; hints: Record<string, string> } => {
      const errors: Record<string, FieldError> = {};
      const hints: Record<string, string> = {};

      // worker_name validation
      const workerName = data.worker_name as string;
      if (workerName !== undefined && workerName !== null) {
        const result = validateWorkerName(workerName, 16);
        if (!result.isValid) {
          errors.worker_name = {
            hasError: true,
            message: t(result.error || 'error_workerNameInvalid'),
          };
        } else {
          hints.worker_name = t(i18nKeys.config.hint_workerName);
        }
      }

      // location_name validation
      const locationName = data.location_name as string;
      if (locationName !== undefined && locationName !== null) {
        const result = validateLocationName(locationName, 16);
        if (!result.isValid) {
          errors.location_name = {
            hasError: true,
            message: t(result.error || 'error_locationNameInvalid'),
          };
        } else {
          hints.location_name = t(i18nKeys.config.hint_locationName);
        }
      }

      // startup_message validation (Lite only)
      if (deviceType === DeviceType.LITE) {
        const startupMessage = data.startup_message as string;
        if (startupMessage !== undefined && startupMessage !== null) {
          // Allow empty string
          if (startupMessage === '') {
            hints.startup_message = t(i18nKeys.config.hint_startupMessage);
          } else {
            const byteLength = getByteLength(startupMessage);
            if (byteLength > 16) {
              errors.startup_message = {
                hasError: true,
                message: t(i18nKeys.config.error_startupMessageLength),
              };
            } else if (!/^[a-zA-Z0-9\-_]+$/.test(startupMessage)) {
              errors.startup_message = {
                hasError: true,
                message: t(i18nKeys.config.error_startupMessageInvalid),
              };
            } else {
              hints.startup_message = t(i18nKeys.config.hint_startupMessage);
            }
          }
        }
      }

      // datalog_interval validation
      const datalogInterval = data.datalog_interval;
      if (datalogInterval === undefined || datalogInterval === null || datalogInterval === '') {
        errors.datalog_interval = {
          hasError: true,
          message: t(i18nKeys.config.error_valueRequired),
        };
      } else {
        const numValue =
          typeof datalogInterval === 'string' ? parseInt(datalogInterval) : datalogInterval;
        if (typeof numValue !== 'number' || isNaN(numValue)) {
          errors.datalog_interval = {
            hasError: true,
            message: t(i18nKeys.config.error_invalidNumber),
          };
        } else {
          const result = validateTimeInterval(numValue, 1, 120);
          if (!result.isValid) {
            errors.datalog_interval = {
              hasError: true,
              message: t(i18nKeys.config.error_datalogInterval),
            };
          } else {
            const range = '1 ~ 120';
            hints.datalog_interval = t(i18nKeys.config.hint_datalogInterval, { range });
          }
        }
      }

      // confidence_interval validation (Mini는 intelliflash_interval 별칭에도 동일하게 반영)
      // 각 대상 키별로 range 위반 시 표시할 메시지 키만 다르다.
      const confidenceInterval = data.confidence_interval ?? data.intelliflash_interval;
      const intervalTargets: Array<{ key: string; rangeError: string }> =
        deviceType === DeviceType.MINI
          ? [
              { key: 'confidence_interval', rangeError: i18nKeys.config.error_confidenceInterval },
              {
                key: 'intelliflash_interval',
                rangeError: i18nKeys.config.error_intelliflashInterval,
              },
            ]
          : [{ key: 'confidence_interval', rangeError: i18nKeys.config.error_confidenceInterval }];

      const setIntervalError = (messageKey: string) => {
        intervalTargets.forEach(({ key }) => {
          errors[key] = { hasError: true, message: t(messageKey) };
        });
      };

      if (
        confidenceInterval === undefined ||
        confidenceInterval === null ||
        confidenceInterval === ''
      ) {
        setIntervalError(i18nKeys.config.error_valueRequired);
      } else {
        const numValue =
          typeof confidenceInterval === 'string'
            ? parseInt(confidenceInterval)
            : confidenceInterval;
        if (typeof numValue !== 'number' || isNaN(numValue)) {
          setIntervalError(i18nKeys.config.error_invalidNumber);
        } else if (!validateTimeInterval(numValue, 15, 60).isValid) {
          intervalTargets.forEach(({ key, rangeError }) => {
            errors[key] = { hasError: true, message: t(rangeError) };
          });
        } else {
          const range = '15 ~ 60';
          intervalTargets.forEach(({ key }) => {
            hints[key] = t(i18nKeys.config.hint_confidenceInterval, { range });
          });
        }
      }

      return { errors, hints };
    },
    [t, deviceType]
  );

  return {
    validateGeneralConfig,
  };
};
